import { Component, DestroyRef, inject, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, catchError, concatMap, EMPTY, finalize, map, Observable, of, Subscription, switchMap, tap } from "rxjs";
import { formatFromDate } from "src/app/common/datetime_utility";
import { ConfirmationDialogComponent } from "src/app/components/confirmation-dialog/confirmation-dialog.component";
import { IntegrationCreatedComponent, IntegrationCreatedInput } from "src/app/components/integration-created/integration-created.component";
import { StatusLabelEnum } from "src/app/components/status-label/status-label.component";
import { Label, Labels } from "src/app/labels/englishLabels";
import { IntegrationStatusModel } from "src/app/models/api";
import {
    CardActionClicked,
    CombinedIntegration,
    getIntegrationStatusLabel,
    IInfoCard,
    IntegrationActionsEnum,
    IntegrationsActionsNamesToLabelsMap,
    StatusCodeEnum,
    StatusReasonEnum,
} from "src/app/models/api/integration.model";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { IIntegrationComponentInput } from "src/app/models/componentInputs";
import { IIntegrationComponentOutput } from "src/app/models/componentInputs/integrationComponentInput.interface";
import { PlatformIconPaths } from "src/app/models/constants/platform.constants";
import { PopStartingHeight, PopUpMaxHeight, PopUpStyling, PopUpWidth, PopUpWidthSmall } from "src/app/models/constants/popup.constants";
import { MenuAction } from "src/app/models/menuAction.model";
import { InternalNavigationTriggerModel } from "src/app/models/navigation/internalNavigationTrigger.model";
import { NavigationParams } from "src/app/models/navigation/navigationParams.interface";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { IntegrationsHttpService } from "src/app/services/httpServices/integrationsHttpsService/integrations-http.service";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import { QueryParamsService } from "src/app/services/queryParams/query-params.service";
import { IntegrationComponent } from "../integration/integration.component";
import { UserService } from "src/app/services/infra/user/user.service";
import { HttpStatusCode } from "@angular/common/http";
import { getPlatformDisplayName } from "src/app/common/platformUtils";
import { AuthenticationMethodToLabelPath } from "src/app/models/constants/credentials.constants";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";
import { ICardField } from "src/app/models/viewModels/cardModel";

@Component({
    selector: "app-integrations",
    templateUrl: "./integrations.component.html",
    styleUrls: ["./integrations.component.scss"],
})
export class IntegrationsComponent implements OnInit, OnDestroy {
    public readonly numOfIntegrationsPerRow: number = 3;
    public integrationsCards$: Observable<IInfoCard[]> = of([]);

    public isIntegrationsScansDataFeatureEnabled: WritableSignal<boolean> = signal<boolean>(false);

    public status = StatusLabelEnum.active;

    // If you change max integrations number please change it in add integration api as well
    public maxIntegrations = 30;

    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    public disabledAddIntegrationButtonReason = "";

    public permissionsEnum = PermissionEnum;

    private _queryParamsSubscription$: Subscription;
    private _lastNavigationParams: NavigationParams = {};

    private _integrationsHttpService = inject(IntegrationsHttpService);
    private _featureFlagsService = inject(FeatureFlagsService);
    private _storeService = inject(StoreService);
    private _destroyRef = inject(DestroyRef);

    constructor(
        public permissionsService: PermissionsService,
        public userService: UserService,
        private _dialogRef: MatDialog,
        private _router: Router,
        private _route: ActivatedRoute,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _queryParamsService: QueryParamsService,
    ) {
        this._queryParamsSubscription$ = this._queryParamsService.toNavigate$.subscribe((navigationParams: NavigationParams) => {
            if (navigationParams.integration) {
                if (!this._canEdit()) {
                    this._removeIdFromUrl();
                } else {
                    // do not trigger dialog open if same id
                    if (navigationParams.integration != this._lastNavigationParams.integration) {
                        this._openIntegrationDialog({
                            integrationId: navigationParams.integration,
                            parentIsLoading: this.isLoading$,
                            isInAddMode: false,
                        });
                    }
                    this._lastNavigationParams = navigationParams;
                }
            } else {
                this._lastNavigationParams = navigationParams;
            }
        });
    }

    ngOnInit(): void {
        this.isIntegrationsScansDataFeatureEnabled.set(this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.IntegrationsScansData));
        this._updateIntegrationsCards();
    }

    ngOnDestroy(): void {
        this._queryParamsSubscription$?.unsubscribe();
    }

    public actionOnClick(event: CardActionClicked) {
        this._handleIntegrationActionClicked(event);
    }

    public addIntegration(): void {
        this._openIntegrationDialog({
            isInAddMode: true,
            parentIsLoading: this.isLoading$,
        });
    }

    private _openIntegrationDialog(input: IIntegrationComponentInput): MatDialogRef<IntegrationComponent, boolean> {
        const dialogRef = this._dialogRef.open(IntegrationComponent, {
            height: PopStartingHeight,
            width: PopUpWidth,
            maxHeight: PopUpMaxHeight,
            minHeight: PopStartingHeight,
            data: input,
            panelClass: PopUpStyling,
        });

        dialogRef
            .afterClosed()
            .pipe(
                tap((output: IIntegrationComponentOutput) => {
                    if (output.refreshNeeded) {
                        this._updateIntegrationsCards();

                        if (input.isInAddMode) {
                            this._openNewIntegrationCreated(output.integrationId);
                        }
                    }

                    this._removeIdFromUrl();
                }),
            )
            .subscribe();

        return dialogRef;
    }

    private _openNewIntegrationCreated(integrationId: string) {
        this._dialogRef.open(IntegrationCreatedComponent, {
            height: "190px",
            width: PopUpWidthSmall,
            data: {
                integrationId: integrationId,
            } as IntegrationCreatedInput,
            panelClass: PopUpStyling,
        });
    }

    private _removeIdFromUrl() {
        this._router.navigate(["."], {
            relativeTo: this._route,
            queryParams: { integration: null },
            queryParamsHandling: "merge",
        });
    }

    private _handleIntegrationActionClicked(actionClicked: CardActionClicked): void {
        const card = actionClicked.card;
        const action = actionClicked.action.name;

        switch (action) {
            case IntegrationActionsEnum.enable.toString():
                this._handleEnableActionClicked(card);
                break;
            case IntegrationActionsEnum.disable.toString():
                this._handleDisableActionClicked(card);
                break;
            case IntegrationActionsEnum.edit.toString():
                break;
            case IntegrationActionsEnum.delete.toString():
                this._handleDeleteActionClicked(card);
                break;
            case IntegrationActionsEnum.retry.toString():
                this._handleRetryAuthActionClicked(card);
                break;
        }
    }

    private _handleEnableActionClicked(integrationCard: IInfoCard): void {
        this._integrationsHttpService
            .enableIntegration(integrationCard.cardId)
            .pipe(
                tap(() => this._updateIntegrationsCards()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    private _handleDisableActionClicked(integrationCard: IInfoCard): void {
        this._integrationsHttpService
            .disableIntegration(integrationCard.cardId)
            .pipe(
                tap(() => this._updateIntegrationsCards()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    private _handleDeleteActionClicked(integrationCard: IInfoCard): void {
        const dialogRef = this._dialogRef.open(ConfirmationDialogComponent, {
            data: {
                title: Labels.integrationsPage.deleteConfirmation.title,
                content: Labels.integrationsPage.deleteConfirmation.content,
            },
            disableClose: false,
            panelClass: "sidepanel-dialog-container",
        });

        dialogRef
            .afterClosed()
            .pipe(
                concatMap((userConfirmed: boolean) => {
                    if (userConfirmed) {
                        return this._integrationsHttpService.deleteIntegration(integrationCard.cardId).pipe(
                            tap(() => this._updateIntegrationsCards()),
                            catchError((error) => {
                                this._httpErrorHandlerService.handle(error);
                                return EMPTY;
                            }),
                            takeUntilDestroyed(this._destroyRef),
                        );
                    }

                    return of(null);
                }),
            )
            .subscribe();
    }

    private _handleRetryAuthActionClicked(integrationCard: IInfoCard): void {
        this._integrationsHttpService
            .retryIntegration(integrationCard.cardId)
            .pipe(
                tap(() => this._updateIntegrationsCards()),
                catchError((error) => {
                    this._httpErrorHandlerService.handle(error, error.status === HttpStatusCode.UnprocessableEntity ? true : false);
                    return EMPTY;
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    private _getActionsMenu(integrationStatus: IntegrationStatusModel) {
        const menuActions: MenuAction[] = [];
        const integrationActions = this._getIntegrationActions(integrationStatus);
        const disableEditIntegration = !this._canEdit();

        integrationActions.forEach((action) => {
            switch (action) {
                case IntegrationActionsEnum.disable.toString():
                    menuActions.push({
                        name: IntegrationActionsEnum.disable,
                        displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.disable],
                        disabled: disableEditIntegration,
                    });
                    break;
                case IntegrationActionsEnum.edit.toString():
                    menuActions.push({
                        name: IntegrationActionsEnum.edit,
                        displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.edit],
                        disabled: disableEditIntegration,
                        shouldOpenDialog: true,
                    });
                    break;
                case IntegrationActionsEnum.enable.toString():
                    menuActions.push({
                        name: IntegrationActionsEnum.enable,
                        displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.enable],
                        disabled: disableEditIntegration,
                    });
                    break;
                case IntegrationActionsEnum.delete.toString():
                    menuActions.push({
                        name: IntegrationActionsEnum.delete,
                        displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.delete],
                        disabled: disableEditIntegration,
                    });
                    break;
                case IntegrationActionsEnum.retry.toString():
                    menuActions.push({
                        name: IntegrationActionsEnum.retry,
                        displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.retry],
                        disabled: disableEditIntegration,
                    });
                    break;
            }
        });

        return menuActions;
    }

    private _updateIntegrationsCards(): void {
        this.integrationsCards$ = this._storeService.updateIntegrations$().pipe(
            tap(() => this.isLoading$.next(true)),
            switchMap((integrations) => {
                if (integrations.length === 0) {
                    this.addIntegration();
                    return EMPTY;
                }

                return of(integrations);
            }),
            map((combinedIntegrations) => this._createIntegrationCards(combinedIntegrations)),
            tap((integrationCards) => {
                if (!this.permissionsService.hasPermissions(this.permissionsEnum.admin)) {
                    this.disabledAddIntegrationButtonReason = this._labelsProviderPipe.transform("integrationsPage.notAllowedToAddIntegrationMsg");
                } else if (integrationCards.length >= this.maxIntegrations) {
                    this.disabledAddIntegrationButtonReason = this._labelsProviderPipe.transform("integrationsPage.maxIntegrationsMsg");
                }
            }),
            finalize(() => this.isLoading$.next(false)),
        );
    }

    private _createIntegrationCards(integrations: CombinedIntegration[]): IInfoCard[] {
        const cards: IInfoCard[] = [];

        integrations.map((integration) => {
            const leftCardFields: ICardField[] = [
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.platformField"),
                    value: getPlatformDisplayName(integration.platform),
                    iconPath: PlatformIconPaths[integration.platform],
                },
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.environmentField"),
                    value: this._beautifyDisplayStringPipe.transform(integration.environment),
                },
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.integrationIdField"),
                    value: integration.id,
                    copyableText: this._labelsProviderPipe.transform("integrationsPage.copyIntegrationsId"),
                    isCopyable: true,
                },
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.integrationMethodField"),
                    value: Label.get(AuthenticationMethodToLabelPath[integration.authenticationMethod]),
                },
            ];

            const rightCardFields: ICardField[] = [
                ...(this.isIntegrationsScansDataFeatureEnabled()
                    ? [
                          {
                              key: this._labelsProviderPipe.transform("integrationsPage.createdAtField"),
                              value: formatFromDate(integration.createdAt),
                          },
                      ]
                    : []),
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.createdByField"),
                    value: integration.createdBy,
                },
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.lastChangedField"),
                    value: formatFromDate(integration.lastUpdated),
                },
                {
                    key: this._labelsProviderPipe.transform("integrationsPage.lastChangedByField"),
                    value: integration.lastUpdatedBy,
                },
            ];

            const integrationCard: IInfoCard = {
                cardId: integration.id,
                primaryCardTitle: integration.name,
                secondaryCardTitle: this.isIntegrationsScansDataFeatureEnabled()
                    ? this._labelsProviderPipe.transform("integrationsPage.lastScan")
                    : this._labelsProviderPipe.transform("integrationsPage.createdAtField"),
                secondaryCardValue: this.isIntegrationsScansDataFeatureEnabled()
                    ? this._displayValueForLastScan(integration.lastScanEndedAt)
                    : formatFromDate(integration.createdAt),
                secondaryCardTooltipText: this._displayValueForDataCollection(integration.dataCollectionStartedAt),
                statusLabel: getIntegrationStatusLabel(integration.connectionStatus),
                statusTooltip: integration.connectionStatus.errorDisplayMessage,
                leftCardFields: leftCardFields,
                rightCardFields: rightCardFields,
                menuActions: this._getActionsMenu(integration.connectionStatus),
                internalNavigation: {
                    query: { integration: integration.id },
                } as InternalNavigationTriggerModel,
            };

            cards.push(integrationCard);
        });

        return cards;
    }

    private _displayValueForLastScan(lastScanEndedAt: string | null | undefined): string {
        switch (lastScanEndedAt) {
            case undefined:
                return this._labelsProviderPipe.transform("integrationsPage.scanDataNotFound");
            case null:
                return this._labelsProviderPipe.transform("integrationsPage.pending1stScanResults");
            default:
                return formatFromDate(new Date(lastScanEndedAt));
        }
    }

    private _displayValueForDataCollection(dataCollectionStartedAt: string | null | undefined): string {
        switch (dataCollectionStartedAt) {
            case undefined:
                return this._labelsProviderPipe.transform("integrationsPage.errorFetchingScanFreshnessData");
            case null:
                return "";
            default:
                return `${this._labelsProviderPipe.transform("integrationsPage.startedCollectingAt")}: ${formatFromDate(new Date(dataCollectionStartedAt))}`;
        }
    }

    private _getIntegrationActions(integrationStatus: IntegrationStatusModel): IntegrationActionsEnum[] {
        const actions = [];

        if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.deactivated) {
            actions.push(IntegrationActionsEnum.enable);
        }

        if (integrationStatus.code === StatusCodeEnum.on) {
            actions.push(IntegrationActionsEnum.disable);
        }

        actions.push(IntegrationActionsEnum.edit);

        if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.failing) {
            actions.push(IntegrationActionsEnum.retry);
        }

        actions.push(IntegrationActionsEnum.delete);
        return actions;
    }

    private _canEdit(): boolean {
        return this.permissionsService.hasPermissions(PermissionEnum.admin);
    }
}
